import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSelector, useDispatch } from 'react-redux';
import { getUserList } from '../../redux/selector';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import { fetchDataAndUpdateStore } from '../../redux/actions';
import apiServices from '../../services/service';
import AddUserForm from './addUserForm';
import ChangePasswordModal from '../settings/changePassword/changePassword';
import ManageRecordsVisibility from './ManageRecordsVisibility';
import { Ic } from '../../shared/component/GosIcon';

/* User Management — GrowthOS design (matched to growthos/Users.jsx). Stat strip + a themed
   table with per-row Active toggle, Change Password, inline Role pill, Manage Records Visibility,
   and a bulk activate/deactivate bar. The design is a mock; this is wired to the REAL user store
   (`userList`) + APIs — nothing fabricated:
   - Active toggle / bulk activate-deactivate → `inActiveUser`
   - inline Role pill → the real multi-role `manageUserRoles` (the base "User" role stays locked,
     matching the legacy Manage-Roles modal); the design's single-select pill is adapted to a
     multi-select checklist because real users hold multiple roles
   - Change Password → the real `ChangePasswordModal` for DB users; Azure/SSO users show a locked
     "SSO" badge (no password to set), matching the legacy `auth_type` gate
   - Manage Records Visibility → the real `ManageRecordsVisibility` component (reused as-is, given a
     themed "Manage" trigger via its new `renderTrigger` prop)
   - New User / Edit → the real `AddUserForm` (register/update, reused as-is — still Bootstrap inside)
   Dropped vs the mock: bulk **Remove** (there is no delete-user API) and the mock Business-Unit
   invite field beyond what the real form carries. */

const AVA_COLORS = ['#c96342', '#2e63a6', '#6a4fae', '#2c7a45', '#b34480', '#9a751c', '#4f6b8f', '#0e8c6f', '#2f7ea3', '#a35a2f'];
const hashIdx = (s) => {
  const str = String(s || '');
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % AVA_COLORS.length;
};
const initials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase() || '?';

function Avatar({ name, size = 34 }) {
  const bg = AVA_COLORS[hashIdx(name)];
  return (
    <span
      className="gos-um-ava"
      style={{ width: size, height: size, background: bg, fontSize: size * 0.4 }}
    >
      {initials(name)}
    </span>
  );
}

function Switch({ on, onClick, title }) {
  return (
    <button type="button" className={'gos-um-sw' + (on ? ' on' : '')} onClick={onClick} title={title} aria-pressed={!!on} />
  );
}

function useOutside(open, onClose) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open, onClose]);
  return ref;
}

/* Filter dropdown (Role / Status / Unit) */
function FilterSelect({ label, value, options, onChange, renderOpt }) {
  const [open, setOpen] = useState(false);
  const ref = useOutside(open, () => setOpen(false));
  const cur = options.find((o) => o.value === value);
  return (
    <div className="gos-um-fs" ref={ref}>
      <button
        type="button"
        className={'gos-um-fs-btn' + (open ? ' on' : '') + (value !== 'all' ? ' has' : '')}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="gos-um-fs-label">{label}: </span>
        <strong>{cur ? cur.label : 'All'}</strong>
        <Ic name="chevD" size={14} />
      </button>
      {open && (
        <div className="gos-um-fs-menu">
          {options.map((o) => (
            <button
              type="button"
              key={o.value}
              className="gos-um-fs-item"
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              <span className={'gos-um-check' + (o.value === value ? ' on' : '')}>
                {o.value === value ? <Ic name="check" size={11} /> : null}
              </span>
              {renderOpt ? renderOpt(o) : o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* Inline role pill → real multi-role assignment (manageUserRoles) */
function RolePill({ user, rolesList, onApplied, setUpdateListFlag }) {
  const [open, setOpen] = useState(false);
  const ref = useOutside(open, () => setOpen(false));
  const userRoleIds = (user.roles || []).map((r) => r.id);
  const [sel, setSel] = useState(userRoleIds);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setSel((user.roles || []).map((r) => r.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const primary = (user.roles || [])[0];
  const primaryName = primary ? primary.roleName : 'No role';
  const extra = (user.roles || []).length - 1;

  const toggle = (role) => {
    if (role.roleValue === 'User') return; // base role locked (matches legacy)
    setSel((prev) => (prev.includes(role.id) ? prev.filter((x) => x !== role.id) : [...prev, role.id]));
  };

  const apply = () => {
    setSaving(true);
    setUpdateListFlag(true);
    apiServices
      .manageUserRoles({ roleIds: sel, userId: user.userId })
      .then((response) => {
        if (response.status === 200) {
          toast.success('Roles updated successfully!', { position: 'top-right' });
          setOpen(false);
          onApplied();
        } else {
          const msgs = [];
          for (const k in response.errors) if (response.errors.hasOwnProperty(k)) msgs.push(...response.errors[k]);
          msgs.forEach((m) => toast.error(m, { position: 'top-right' }));
        }
      })
      .catch((e) => toast.error(`${e.message}`, { position: 'top-right' }))
      .finally(() => setSaving(false));
  };

  return (
    <div className="gos-um-rolewrap" ref={ref}>
      <button type="button" className={'gos-um-role-pill' + (open ? ' on' : '')} onClick={() => setOpen((o) => !o)}>
        <span className="gos-um-dot" style={{ background: AVA_COLORS[hashIdx(primaryName)] }} />
        {primaryName}
        {extra > 0 ? <span className="gos-um-role-more">+{extra}</span> : null}
        <Ic name="chevD" size={13} className="gos-um-role-caret" />
      </button>
      {open && (
        <div className="gos-um-role-menu">
          <div className="gos-um-menu-head">Assign roles</div>
          <div className="gos-um-role-list">
            {rolesList.map((r) => {
              const on = sel.includes(r.id);
              const locked = r.roleValue === 'User';
              return (
                <button
                  type="button"
                  key={r.id}
                  className={'gos-um-role-opt' + (locked ? ' locked' : '')}
                  onClick={() => toggle(r)}
                >
                  <span className={'gos-um-check' + (on ? ' on' : '')}>{on ? <Ic name="check" size={11} /> : null}</span>
                  <span className="gos-um-dot" style={{ background: AVA_COLORS[hashIdx(r.roleName)] }} />
                  <span className="gos-um-role-optname">{r.roleName}</span>
                  {locked ? <Ic name="lock" size={12} /> : null}
                </button>
              );
            })}
          </div>
          <div className="gos-um-role-foot">
            <button type="button" className="gos-btn gos-btn-ghost gos-btn-sm" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button type="button" className="gos-btn gos-btn-primary gos-btn-sm" disabled={saving} onClick={apply}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const initialValue = {
  empId: '',
  userName: '',
  mobile: '',
  email: '',
  oid: '',
  given_name: '',
  family_name: '',
  preferred_username: '',
  firstName: '',
  lastName: '',
  secondaryEmail: '',
  phoneNumber: '',
  extension: '',
  display_name: '',
  roles: [],
  accessLevel: '',
};

function UserManagementList({ embedded = false }) {
  const dispatch = useDispatch();
  const userList = useSelector(getUserList);
  const stateData = useSelector((state) => state);
  const storeLoading = isLoadingByKey(stateData, 'userList');

  const [rowData, setRowData] = useState(userList);
  const [rolesList, setRolesList] = useState([]);
  const [updateListFlag, setUpdateListFlag] = useState(false);

  const [q, setQ] = useState('');
  const [roleF, setRoleF] = useState('all');
  const [statusF, setStatusF] = useState('all');
  const [buF, setBuF] = useState('all');
  const [sel, setSel] = useState(() => new Set());

  const [open, setOpen] = useState(false);
  const [buttontype, setButtontype] = useState(null);
  const [userData, setUserData] = useState();
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    setRowData(userList);
  }, [userList, updateListFlag]);

  // roles for the inline pill
  useEffect(() => {
    apiServices
      .getRoleList()
      .then((res) => {
        if (res.status === 200) setRolesList(res.data.rolesList || []);
      })
      .catch((e) => console.error('getRoleList', e));
  }, []);

  // refetch list after a create/update/role/status change (app-wide fetch covers first load)
  useEffect(() => {
    if (updateListFlag) {
      dispatch(fetchDataAndUpdateStore({ key: 'userList' }));
      setUpdateListFlag(false);
    }
  }, [dispatch, updateListFlag]);

  const nameOf = (u) => u.display_name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '—';
  const userNameOf = (u) => u.userName || u.preferred_username || u.email || '';

  /* filtered rows */
  const rows = useMemo(() => {
    let out = rowData || [];
    const s = q.trim().toLowerCase();
    if (s)
      out = out.filter((u) =>
        `${u.firstName || ''} ${u.lastName || ''} ${nameOf(u)} ${u.email || ''} ${userNameOf(u)} ${u.empId || ''}`
          .toLowerCase()
          .includes(s)
      );
    if (roleF !== 'all') out = out.filter((u) => (u.roles || []).some((r) => r.roleName === roleF));
    if (statusF !== 'all') out = out.filter((u) => (statusF === 'active' ? u.isActive : !u.isActive));
    if (buF !== 'all') out = out.filter((u) => (u.businessUnit || '—') === buF);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowData, q, roleF, statusF, buF]);

  const stats = useMemo(
    () => ({
      total: (rowData || []).length,
      active: (rowData || []).filter((u) => u.isActive).length,
      inactive: (rowData || []).filter((u) => !u.isActive).length,
      units: new Set((rowData || []).map((u) => u.businessUnit).filter(Boolean)).size,
    }),
    [rowData]
  );

  const buOpts = useMemo(
    () => Array.from(new Set((rowData || []).map((u) => u.businessUnit || '—'))).sort(),
    [rowData]
  );
  const activeFilters = (roleF !== 'all') + (statusF !== 'all') + (buF !== 'all');

  const allSel = rows.length > 0 && rows.every((u) => sel.has(u.userId));
  const toggleAll = () => setSel(allSel ? new Set() : new Set(rows.map((u) => u.userId)));
  const toggle = (id) =>
    setSel((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  /* Active toggle → inActiveUser */
  const setActive = (u, isActive) => {
    apiServices
      .inActiveUser({ userId: u.userId, isActive })
      .then((response) => {
        if (response?.status === 200) {
          toast.success(`User ${isActive ? 'Activated' : 'Deactivated'} Successfully!`, { position: 'top-right' });
          setUpdateListFlag(true);
        } else {
          toast.error('Failed to update user status!', { position: 'top-right' });
        }
      })
      .catch(() => toast.error('Failed to update user status!', { position: 'top-right' }));
  };

  const bulkSetActive = (isActive) => {
    const ids = [...sel];
    if (!ids.length) return;
    Promise.all(ids.map((id) => apiServices.inActiveUser({ userId: id, isActive }).catch(() => null)))
      .then(() => {
        toast.success(`${ids.length} user${ids.length > 1 ? 's' : ''} ${isActive ? 'activated' : 'deactivated'}`, {
          position: 'top-right',
        });
        setUpdateListFlag(true);
        setSel(new Set());
      });
  };

  /* Add / Edit user (real AddUserForm) */
  const handleOpenAddForm = () => {
    setButtontype('add');
    setUserData({ ...initialValue, type: 'add' });
    setOpen(true);
  };
  const handleClickOpenEditForm = (paramsValue) => {
    setButtontype('edit');
    setUserData({ ...paramsValue, type: 'edit' });
    setOpen(true);
  };
  const handleCloseAddForm = () => setOpen(false);

  const handleFormSubmit = (formData) => {
    const editedFormData = { ...formData };
    editedFormData.mobile = formData.phoneNumber;
    editedFormData.display_name = formData.userName;

    if (editedFormData.type === 'edit') {
      const userUpdateData = {
        userId: editedFormData?.userId,
        phoneNumber: editedFormData?.phoneNumber,
        email: editedFormData?.email,
        empId: editedFormData?.empId,
        designationId: editedFormData?.designationId,
        firstName: editedFormData?.firstName,
        lastName: editedFormData?.lastName,
        auth_type: editedFormData?.auth_type,
        password: editedFormData?.password,
        accessLevel: editedFormData?.accessLevel,
        geoScopeNodeId: editedFormData?.geoScopeNodeId,
        orgScopeNodeId: editedFormData?.orgScopeNodeId,
      };
      apiServices
        .updateUser(userUpdateData)
        .then((response) => {
          if (response.status === 200) {
            setOpen(false);
            setUpdateListFlag(true);
            toast.success('User Updated successfully!', { position: 'top-right' });
          } else {
            setOpen(false);
            const msgs = [];
            for (const k in response.errors) if (response.errors.hasOwnProperty(k)) msgs.push(...response.errors[k]);
            msgs.forEach((m) => toast.error(m, { position: 'top-right' }));
          }
        })
        .catch((error) => {
          if (error.response?.data?.errors) {
            Object.keys(error.response.data.errors).forEach((t) =>
              error.response.data.errors[t].forEach((m) => toast.error(` ${m}`, { position: 'top-right' }))
            );
          }
        });
    } else {
      const newObj1 = { ...editedFormData };
      delete newObj1.type;
      delete newObj1.flagName;
      newObj1.mobile = newObj1.phoneNumber;
      newObj1.display_name = newObj1.userName;
      apiServices
        .registerUser(newObj1)
        .then((response) => {
          if (response.status === 200) {
            setOpen(false);
            setUpdateListFlag(true);
            toast.success('User created successfully!', { position: 'top-right' });
          } else {
            setOpen(false);
            const msgs = [];
            for (const k in response.errors) if (response.errors.hasOwnProperty(k)) msgs.push(...response.errors[k]);
            msgs.forEach((m) => toast.error(m, { position: 'top-right' }));
          }
        })
        .catch((error) => {
          if (error.response && error.response.data.message) {
            toast.error(` ${error.response.data.message}`, { position: 'top-right' });
          } else {
            toast.error(`${error?.response?.data?.title}`, { position: 'top-right' });
          }
        });
    }
  };

  const selCount = sel.size;

  const body = (
    <div className="gos-um">
      <div className="gos-um-head">
        <div>
          <div className="gos-um-title">Users</div>
          <div className="gos-um-sub">
            Manage who has access to your workspace, their roles, and record visibility.
          </div>
        </div>
        <button type="button" className="gos-btn gos-btn-primary" onClick={handleOpenAddForm}>
          <Ic name="plus" size={16} />
          New User
        </button>
      </div>

      <div className="gos-um-stats">
        {[
          ['Total users', stats.total, 'leads', 'var(--gos-text-3)'],
          ['Active', stats.active, 'checkCircle', '#2c7a45'],
          ['Inactive', stats.inactive, 'logout', 'var(--gos-text-3)'],
          ['Business units', stats.units, 'building', '#6a4fae'],
        ].map(([label, val, icon, col]) => (
          <div key={label} className="gos-um-stat">
            <span className="gos-um-stat-ic" style={{ color: col }}>
              <Ic name={icon} size={17} />
            </span>
            <div>
              <div className="gos-um-stat-val">{val}</div>
              <div className="gos-um-stat-label">{label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="gos-um-card">
        {selCount === 0 ? (
          <div className="gos-um-toolbar">
            <div className="gos-um-search">
              <Ic name="search" size={16} />
              <input placeholder="Search in Users…" value={q} onChange={(e) => setQ(e.target.value)} />
              {q ? (
                <button type="button" className="gos-um-clear" onClick={() => setQ('')}>
                  <Ic name="x" size={14} />
                </button>
              ) : null}
            </div>
            <span className="gos-um-count">
              <strong>{rows.length}</strong>
              {q.trim() || activeFilters ? ' matches' : ' users'}
            </span>
            <div className="gos-um-grow" />
            <FilterSelect
              label="Role"
              value={roleF}
              options={[{ value: 'all', label: 'All' }].concat(rolesList.map((r) => ({ value: r.roleName, label: r.roleName })))}
              onChange={setRoleF}
            />
            <FilterSelect
              label="Status"
              value={statusF}
              options={[
                { value: 'all', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
              onChange={setStatusF}
            />
            <FilterSelect
              label="Unit"
              value={buF}
              options={[{ value: 'all', label: 'All' }].concat(buOpts.map((b) => ({ value: b, label: b })))}
              onChange={setBuF}
            />
            {activeFilters ? (
              <button
                type="button"
                className="gos-um-link"
                onClick={() => {
                  setRoleF('all');
                  setStatusF('all');
                  setBuF('all');
                }}
              >
                Clear
              </button>
            ) : null}
          </div>
        ) : (
          <div className="gos-um-bulk">
            <label className="gos-um-bulk-sel" onClick={() => setSel(new Set())}>
              <span className="gos-um-check on">
                <Ic name="check" size={12} />
              </span>
              <span className="gos-um-bulk-count">{selCount} selected</span>
            </label>
            <div className="gos-um-grow" />
            <button type="button" className="gos-btn gos-btn-ghost gos-btn-sm" onClick={() => bulkSetActive(true)}>
              <Ic name="checkCircle" size={14} />
              Activate
            </button>
            <button type="button" className="gos-btn gos-btn-ghost gos-btn-sm" onClick={() => bulkSetActive(false)}>
              <Ic name="logout" size={14} />
              Deactivate
            </button>
            <button type="button" className="gos-um-iconbtn" onClick={() => setSel(new Set())} title="Clear selection">
              <Ic name="x" size={16} />
            </button>
          </div>
        )}

        <div className="gos-um-tblscroll">
          <table className="gos-um-tbl">
            <thead>
              <tr>
                <th className="gos-um-th-check">
                  <span
                    className={'gos-um-check' + (allSel ? ' on' : '')}
                    onClick={toggleAll}
                    style={{ cursor: 'pointer' }}
                  >
                    {allSel ? <Ic name="check" size={12} /> : null}
                  </span>
                </th>
                <th>Actions</th>
                <th style={{ textAlign: 'center' }}>Active</th>
                <th>Change Password</th>
                <th>Role</th>
                <th>Record Visibility</th>
                <th>User</th>
                <th>Emp ID</th>
                <th>User Name</th>
                <th>Email</th>
                <th>Business Unit</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                const isDb = String(u.auth_type || '').toLowerCase() === 'db';
                return (
                  <tr
                    key={u.userId}
                    className={(sel.has(u.userId) ? 'gos-um-row-sel ' : '') + (u.isActive ? '' : 'gos-um-row-off')}
                  >
                    <td>
                      <span
                        className={'gos-um-check' + (sel.has(u.userId) ? ' on' : '')}
                        onClick={() => toggle(u.userId)}
                        style={{ cursor: 'pointer' }}
                      >
                        {sel.has(u.userId) ? <Ic name="check" size={12} /> : null}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="gos-um-icon-act"
                        title="Edit user"
                        onClick={() => handleClickOpenEditForm(u)}
                      >
                        <Ic name="edit" size={15} />
                      </button>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Switch on={!!u.isActive} onClick={() => setActive(u, !u.isActive)} title={u.isActive ? 'Active' : 'Inactive'} />
                    </td>
                    <td>
                      {isDb ? (
                        <button
                          type="button"
                          className="gos-um-mng-btn"
                          onClick={() => {
                            setSelectedUser(u);
                            setPasswordModalOpen(true);
                          }}
                        >
                          <Ic name="lock" size={14} />
                          Set
                        </button>
                      ) : (
                        <span className="gos-um-sso">
                          <Ic name="lock" size={13} />
                          SSO
                        </span>
                      )}
                    </td>
                    <td>
                      <RolePill
                        user={u}
                        rolesList={rolesList}
                        onApplied={() => setUpdateListFlag(true)}
                        setUpdateListFlag={setUpdateListFlag}
                      />
                    </td>
                    <td>
                      <ManageRecordsVisibility
                        rowData={u}
                        setUpdateListFlag={setUpdateListFlag}
                        renderTrigger={(openModal) => (
                          <button type="button" className="gos-um-mng-btn" onClick={openModal}>
                            <Ic name="eye" size={14} />
                            Manage
                          </button>
                        )}
                      />
                    </td>
                    <td>
                      <div className="gos-um-cell-lead">
                        <Avatar name={nameOf(u)} size={34} />
                        <button type="button" className="gos-um-name-link" onClick={() => handleClickOpenEditForm(u)}>
                          {nameOf(u)}
                        </button>
                      </div>
                    </td>
                    <td>{u.empId ? <span className="gos-um-mono">{u.empId}</span> : <span className="gos-um-faint">—</span>}</td>
                    <td>
                      <span className="gos-um-cell gos-um-muted">{userNameOf(u)}</span>
                    </td>
                    <td>
                      <span className="gos-um-cell gos-um-muted">{u.email}</span>
                    </td>
                    <td>
                      {u.businessUnit ? (
                        <span className="gos-um-bu">{u.businessUnit}</span>
                      ) : (
                        <span className="gos-um-faint">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 ? (
            <div className="gos-um-empty">
              {storeLoading ? 'Loading users…' : 'No users match your filters.'}
            </div>
          ) : null}
        </div>

        <div className="gos-um-foot">
          <span>
            Showing <strong>{rows.length}</strong> of <strong>{(rowData || []).length}</strong> users
          </span>
        </div>
      </div>

      {open && (
        <AddUserForm
          handleCloseAddForm={handleCloseAddForm}
          handleFormSubmit={handleFormSubmit}
          userData={userData}
          open={open}
        />
      )}

      <ChangePasswordModal
        show={passwordModalOpen}
        selectedUser={selectedUser}
        onHide={() => setPasswordModalOpen(false)}
        changePasswordAdmin={true}
      />
    </div>
  );

  return embedded ? body : <div className="inner-page">{body}</div>;
}

export default UserManagementList;
